import { addToast } from '@ydistri/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store';
import { buildAdditiveMultiselectSelection } from './configurationMultiselectModalLib';
import {
  createResolvedPasteResponse,
  extractPastedValues,
  parseTabularDataFromHtml,
} from './configurationMultiselectPaste';
import type {
  ConfigurationMultiselectProps,
  MultiselectCustomerPartialEntity,
  MultiselectParseResponse,
} from './configurationMultiselectTypes';

interface UseConfigurationMultiselectPasteOptions<T extends MultiselectCustomerPartialEntity> {
  compareAsInt: boolean;
  data: T[];
  dataCheck?: T[];
  onResolvedPastedData?: ConfigurationMultiselectProps<T>['onResolvedPastedData'];
  onValueChange: ConfigurationMultiselectProps<T>['onValueChange'];
  resolutionContextKey?: ConfigurationMultiselectProps<T>['resolutionContextKey'];
  resolvePastedData?: ConfigurationMultiselectProps<T>['resolvePastedData'];
  value: number[];
}

interface UseConfigurationMultiselectPasteResult<T extends MultiselectCustomerPartialEntity> {
  clearParsedData: () => void;
  effectiveData: T[];
  handlePastedData: (pastedData: string) => Promise<void>;
  isResolvingPastedData: boolean;
  parseResponse?: MultiselectParseResponse;
  setParsedData: (value: number[]) => void;
}

const useConfigurationMultiselectPaste = <T extends MultiselectCustomerPartialEntity>({
  compareAsInt,
  data,
  dataCheck,
  onResolvedPastedData,
  onValueChange,
  resolutionContextKey,
  resolvePastedData,
  value,
}: UseConfigurationMultiselectPasteOptions<T>): UseConfigurationMultiselectPasteResult<T> => {
  const dispatch = useAppDispatch();
  const [parseResponse, setParseResponse] = useState<MultiselectParseResponse>();
  const [resolvedPastedData, setResolvedPastedData] = useState<T[]>([]);
  const [isResolvingPastedData, setIsResolvingPastedData] = useState(false);
  const mountedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const previousContextKeyRef = useRef(resolutionContextKey);
  const onResolvedPastedDataRef = useRef(onResolvedPastedData);
  const onValueChangeRef = useRef(onValueChange);
  const valueRef = useRef(value);

  onResolvedPastedDataRef.current = onResolvedPastedData;
  onValueChangeRef.current = onValueChange;
  valueRef.current = value;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (Object.is(previousContextKeyRef.current, resolutionContextKey)) return;

    previousContextKeyRef.current = resolutionContextKey;
    requestSequenceRef.current += 1;
    setResolvedPastedData([]);
    setParseResponse(undefined);
    setIsResolvingPastedData(false);
  }, [resolutionContextKey]);

  const effectiveData = useMemo(() => {
    const byId = new Map<number, T>();
    resolvedPastedData.forEach(item => byId.set(item.id, item));
    data.forEach(item => byId.set(item.id, item));
    return [...byId.values()];
  }, [data, resolvedPastedData]);

  const notifyDuplicateIds = useCallback(
    (duplicateIds: string[]) => {
      if (duplicateIds.length === 0) return;

      dispatch(
        addToast({
          message: `Some of the pasted IDs were duplicated: ${duplicateIds.join(', ')}`,
          isError: true,
        }),
      );
    },
    [dispatch],
  );

  const handlePastedData = useCallback(
    async (pastedData: string) => {
      if (resolvePastedData) {
        const { values, duplicateIds } = extractPastedValues(pastedData);
        if (values.length === 0) return;

        const requestId = ++requestSequenceRef.current;
        setIsResolvingPastedData(true);

        try {
          const resolved = await resolvePastedData(values);
          if (!mountedRef.current || requestId !== requestSequenceRef.current) return;

          const parsedEvent = createResolvedPasteResponse(values, duplicateIds, resolved);
          setResolvedPastedData(current => {
            const byId = new Map(current.map(item => [item.id, item]));
            resolved.data.forEach(item => byId.set(item.id, item));
            return [...byId.values()];
          });
          onResolvedPastedDataRef.current?.(resolved.data);

          if (parsedEvent.notRecognized.length > 0) {
            setParseResponse(parsedEvent);
          } else {
            onValueChangeRef.current(
              buildAdditiveMultiselectSelection(valueRef.current, parsedEvent.ids),
            );
          }
          notifyDuplicateIds(parsedEvent.duplicateIds);
        } catch {
          if (!mountedRef.current || requestId !== requestSequenceRef.current) return;

          dispatch(
            addToast({
              message: 'Unable to resolve pasted data. Please try again.',
              isError: true,
            }),
          );
        } finally {
          if (mountedRef.current && requestId === requestSequenceRef.current) {
            setIsResolvingPastedData(false);
          }
        }

        return;
      }

      const parsedEvent = parseTabularDataFromHtml(
        effectiveData,
        pastedData,
        compareAsInt,
        dataCheck,
      );

      if (parsedEvent.notRecognized.length > 0 || parsedEvent.dataCheckOnlyIds.length > 0) {
        setParseResponse(parsedEvent);
      } else {
        onValueChangeRef.current(
          buildAdditiveMultiselectSelection(valueRef.current, parsedEvent.ids),
        );
        notifyDuplicateIds(parsedEvent.duplicateIds);
      }
    },
    [compareAsInt, dataCheck, dispatch, effectiveData, notifyDuplicateIds, resolvePastedData],
  );

  const clearParsedData = useCallback(() => {
    setParseResponse(undefined);
  }, []);

  const setParsedData = useCallback((newValue: number[]) => {
    setParseResponse(undefined);
    onValueChangeRef.current(newValue);
  }, []);

  return {
    clearParsedData,
    effectiveData,
    handlePastedData,
    isResolvingPastedData,
    parseResponse,
    setParsedData,
  };
};

export default useConfigurationMultiselectPaste;
